import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Custom hook for AI meeting features - summary and assistant
 */
export function useAIAssistant(roomId) {
    const [isLoading, setIsLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [insights, setInsights] = useState(null);
    const [chatHistory, setChatHistory] = useState([]);
    const [error, setError] = useState(null);

    // Generate meeting summary
    const generateSummary = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await api.post(`/rooms/${roomId}/summary`);
            setSummary(response.data);
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'Failed to generate summary';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [roomId]);

    // Ask AI assistant a question
    const askAssistant = useCallback(async (question) => {
        setIsLoading(true);
        setError(null);

        // Add user message to chat
        const userMessage = {
            role: 'user',
            content: question,
            timestamp: new Date().toISOString(),
        };
        setChatHistory(prev => [...prev, userMessage]);

        try {
            const response = await api.post(`/rooms/${roomId}/assistant`, { question });

            // Add AI response to chat
            const aiMessage = {
                role: 'assistant',
                content: response.data.answer,
                timestamp: new Date().toISOString(),
            };
            setChatHistory(prev => [...prev, aiMessage]);

            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'Failed to get response';
            setError(errorMessage);

            // Add error message to chat
            const errorMsg = {
                role: 'assistant',
                content: `Sorry, I couldn't process that request: ${errorMessage}`,
                timestamp: new Date().toISOString(),
                isError: true,
            };
            setChatHistory(prev => [...prev, errorMsg]);

            throw new Error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [roomId]);

    // Get real-time insights
    const getInsights = useCallback(async () => {
        try {
            const response = await api.get(`/rooms/${roomId}/insights`);
            setInsights(response.data);
            return response.data;
        } catch (err) {
            console.error('Failed to get insights:', err);
            return null;
        }
    }, [roomId]);

    // Clear chat history
    const clearChat = useCallback(() => {
        setChatHistory([]);
        setError(null);
    }, []);

    return {
        isLoading,
        summary,
        insights,
        chatHistory,
        error,
        generateSummary,
        askAssistant,
        getInsights,
        clearChat,
        clearSummary: () => setSummary(null),
    };
}

export default useAIAssistant;
